import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Radio, ShieldCheck, LogOut, QrCode } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

const TILE_KEYS = Array.from({ length: 12 }, (_, i) => `tile-${i}`);

export default function Watch() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/stream/access")
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 402) {
          toast.error("PPV access required");
          navigate("/checkout", { replace: true });
        } else {
          setError(err?.response?.data?.detail || "Unable to load stream");
        }
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] text-white">
        <div className="text-center max-w-md">
          <p className="eyebrow text-rose-500">Access blocked</p>
          <h2 className="font-display text-4xl mt-4">{error}</h2>
          <button onClick={() => navigate("/login")} className="btn-ghost mt-8 px-6 py-3 text-xs" data-testid="watch-error-login">
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  // build watermark tile (rendered only if backend says watermark_enabled)
  const watermarkEnabled = !!data.watermark_enabled;
  const watermark = data.watermark || "";
  const tiles = TILE_KEYS;
  const signupUrl = `${window.location.origin}/signup`;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="watch-logo">
            <Logo size={32} />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-widest">
              {data.is_live ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                  Live Now
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-zinc-500" />
                  Off Air
                </>
              )}
            </span>
            {user?.is_admin && (
              <Link to="/admin" className="btn-ghost px-4 py-2 text-xs" data-testid="watch-admin-link">
                Admin
              </Link>
            )}
            <button
              onClick={logout}
              className="btn-ghost px-4 py-2 text-xs flex items-center gap-2"
              data-testid="watch-logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid lg:grid-cols-12 gap-8">
        <section className="lg:col-span-8" data-testid="watch-player-section">
          <p className="eyebrow text-rose-500">Now Streaming</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 uppercase leading-none">
            {data.event_title}
          </h1>
          <p className="text-zinc-400 mt-3">{data.event_subtitle}</p>

          <div
            className="relative mt-8 w-full bg-black border border-white/10"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="stream-player"
          >
            {data.stream_embed_url ? (
              <iframe
                title="PPV Stream"
                src={data.stream_embed_url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-zinc-500 text-sm uppercase tracking-widest">
                Stream not configured
              </div>
            )}

            {/* Watermark overlay — only when admin has watermarking enabled */}
            {watermarkEnabled && watermark && (
              <div className="watermark" data-testid="watermark-overlay">
                {tiles.map((id, i) => {
                  const top = (i * 13) % 90;
                  const left = (i * 23) % 85;
                  return (
                    <span
                      key={id}
                      style={{
                        top: `${top}%`,
                        left: `${left}%`,
                        transform: `rotate(-18deg)`,
                      }}
                    >
                      {watermark}
                    </span>
                  );
                })}
                <span className="drift">{watermark}</span>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-3 text-xs text-zinc-500">
            <ShieldCheck size={14} />
            {watermarkEnabled
              ? "Stream protected by your account watermark. Sharing this screen distributes your email to the recipient."
              : "Private stream — locked to your account."}
          </div>

          {/* QR — sign up for next year */}
          <div className="mt-10 card-flat p-6 md:p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="next-year-qr">
            <div className="bg-white p-3 rounded-sm">
              <QRCodeSVG
                value={signupUrl}
                size={140}
                level="M"
                marginSize={1}
                data-testid="next-year-qr-svg"
              />
            </div>
            <div className="text-center sm:text-left">
              <p className="eyebrow text-rose-500 flex items-center justify-center sm:justify-start gap-2">
                <QrCode size={12} /> Next Year&apos;s Corporate Clash
              </p>
              <h3 className="font-display text-3xl md:text-4xl mt-3 uppercase leading-tight">
                Want to be part of the action?
              </h3>
              <p className="mt-3 text-sm text-zinc-400 max-w-md">
                Scan to sign up for next year&apos;s event — we&apos;ll be in touch
                about training, the fight card and what comes next.
              </p>
              <a
                href={signupUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost mt-5 inline-flex px-5 py-2 text-xs"
                data-testid="next-year-link-btn"
              >
                Or open the form
              </a>
            </div>
          </div>
        </section>

        <aside className="lg:col-span-4 space-y-6" data-testid="watch-side-panel">
          <div className="card-flat p-6">
            <p className="eyebrow text-zinc-500">Your Pass</p>
            <p className="font-display text-3xl mt-2 break-all">{user?.email}</p>
            <div className="mt-5 text-sm text-zinc-400 space-y-2">
              <div className="flex justify-between">
                <span>Status</span>
                <span className="text-emerald-400 uppercase tracking-widest text-xs">
                  Active
                </span>
              </div>
              <div className="flex justify-between">
                <span>Devices</span>
                <span>1 of 1</span>
              </div>
            </div>
          </div>

          <div className="card-flat p-6">
            <p className="eyebrow text-zinc-500 flex items-center gap-2">
              <Radio size={12} /> Event Info
            </p>
            <ul className="mt-4 text-sm text-zinc-300 space-y-3">
              <li><span className="text-zinc-500">Event:</span> {data.event_title}</li>
              <li className="text-zinc-400">{data.event_subtitle}</li>
              <li>
                <span className="text-zinc-500">Status:</span>{" "}
                {data.is_live ? "Live now" : "Off air"}
              </li>
              <li><span className="text-zinc-500">Replay:</span> 48 hours post-event</li>
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}
